/**
 * @description Appends a classic script tag and resolves when loaded (sync execution order preserved by awaiting).
 * @param src Absolute script URL.
 * @return Promise that settles when the script has loaded.
 */
function AppendScript( src: string ): Promise<void> {

  return new Promise( ( resolve, reject ) => {

    const existing = Array.from( document.querySelectorAll( 'script[src]' ) ).find( ( el ) => {

      try {

        return new URL( ( el as HTMLScriptElement ).src ).pathname === new URL( src ).pathname;
      } catch {

        return false;
      }
    } );

    if ( existing ) {

      resolve();

      return;
    }

    const scriptEl = document.createElement( 'script' );
    scriptEl.src = src;
    scriptEl.async = false;
    scriptEl.onload = () => resolve();
    scriptEl.onerror = () => reject( new Error( `Failed to load script: ${ src }` ) );
    document.head.appendChild( scriptEl );
  } );
}

/**
 * @description Loads Webflow rspack schunks and bootstrap in order after jQuery (still loaded via angular.json `scripts`).
 * Keeps heavy Webflow bundles out of the Angular `scripts-*.js` budget chunk while preserving `window.Webflow` for IX2 rebind.
 * @return Promise that settles when Webflow runtime scripts have executed.
 */
export async function LoadWebflowRuntimeBundle(): Promise<void> {

  if ( typeof window === 'undefined' ) {

    return;
  }

  const wf = ( window as Window & { Webflow?: { require?: unknown } } ).Webflow;

  if ( wf?.require ) {

    return;
  }

  const baseEl = document.querySelector( 'base' );
  const baseHref = baseEl?.href ?? `${ window.location.origin }/`;
  const relativePaths = [
    'assets/js/webflow.schunk.b5641674105b3a84.js',
    'assets/js/webflow.schunk.cf92cf9e70479ce4.js',
    'assets/js/webflow-script.js'
  ];

  for ( const relativePath of relativePaths ) {

    const url = new URL( relativePath, baseHref ).href;
    await AppendScript( url );
  }
}
